import React, { useState, useEffect } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  Loader,
  Info,
  Database,
  History,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  XCircle,
  Clock,
  User as UserIcon,
  FileText
} from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import api from '../../api';
import toast from 'react-hot-toast';

interface ImportHistoryItem {
  id: number;
  original_filename: string;
  file_format: string;
  file_size_kb: number;
  import_status: 'success' | 'partial' | 'failed';
  processes_imported: number;
  process_steps_imported: number;
  risks_imported: number;
  controls_imported: number;
  warnings: string[];
  error_message: string | null;
  import_duration_ms: number;
  imported_at: string;
  user: {
    name: string;
    email: string;
  };
}

const TemplateImportTab: React.FC = () => {
  const { currentProject } = useProject();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<any>(null);
  const [history, setHistory] = useState<ImportHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  useEffect(() => {
    if (currentProject?.id) {
      loadHistory();
    }
  }, [currentProject?.id]);

  const loadHistory = async () => {
    if (!currentProject?.id) return;

    try {
      setLoadingHistory(true);
      const response = await api.get(`/api/risk-template-qc/import-history/${currentProject.id}?limit=20`);
      if (response.data.history) {
        setHistory(response.data.history);
      }
    } catch (error) {
      console.error('Failed to load import history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
        return;
      }

      setSelectedFile(file);
      setImportSummary(null);
    }
  };

  const handleImportTemplate = async () => {
    if (!selectedFile || !currentProject) {
      toast.error('Please select a file and ensure a project is selected');
      return;
    }

    try {
      setImporting(true);

      const formData = new FormData();
      formData.append('template', selectedFile);
      formData.append('projectId', currentProject.id.toString());

      toast.loading('Importing template data...', { id: 'template-import' });

      const response = await api.post('/api/risk-template-qc/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportSummary(response.data.summary);

      toast.success(
        response.data.message || 'Template imported successfully!',
        { id: 'template-import', duration: 8000 }
      );

      await loadHistory();

    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(
        error.response?.data?.error || 'Failed to import template',
        { id: 'template-import' }
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-green-600" />
          Import Pre-Filled Risk Template
        </h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">What does this do?</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Automatically extracts data from all sheets (Process, Process Steps, Risks, Controls)</li>
                <li>Saves data directly to the database</li>
                <li>Pre-populates all assessment screens</li>
                <li>No manual re-entry required!</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label
              htmlFor="import-file-upload"
              className="flex-1 cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-green-500 transition-colors"
            >
              <div className="flex flex-col items-center justify-center text-center">
                <Upload className="w-10 h-10 text-gray-400 mb-2" />
                {selectedFile ? (
                  <>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      Click to upload pre-filled Excel template
                    </p>
                    <p className="text-xs text-gray-500">
                      .xlsx or .xls - Max 10MB
                    </p>
                  </>
                )}
              </div>
              <input
                id="import-file-upload"
                type="file"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
              />
            </label>
          </div>

          <button
            onClick={handleImportTemplate}
            disabled={!selectedFile || importing || !currentProject}
            className="w-full bg-green-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {importing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Importing Data...</span>
              </>
            ) : (
              <>
                <Database className="w-5 h-5" />
                <span>Import Template Data</span>
              </>
            )}
          </button>
        </div>
      </div>

      {importSummary && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-900 mb-2">
                ✅ Template Imported Successfully!
              </h3>
              <p className="text-sm text-green-800 mb-4">
                All data from your pre-filled template has been extracted and saved to the database.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="text-3xl font-bold text-green-700">{importSummary.processes}</div>
                  <div className="text-sm text-green-600 mt-1">Processes</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="text-3xl font-bold text-green-700">{importSummary.processSteps}</div>
                  <div className="text-sm text-green-600 mt-1">Process Steps</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="text-3xl font-bold text-green-700">{importSummary.risks}</div>
                  <div className="text-sm text-green-600 mt-1">Risks</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="text-3xl font-bold text-green-700">{importSummary.controls}</div>
                  <div className="text-sm text-green-600 mt-1">Controls</div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-green-200">
                <p className="text-sm text-green-800">
                  <strong>Next Steps:</strong> Navigate to Process Builder, Risk Assessment, or Controls Overview to view your imported data.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <History className="w-5 h-5 text-blue-600" />
          Import History (Last 20)
        </h3>

        {loadingHistory ? (
          <div className="text-center py-8">
            <Loader className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
            <p className="text-sm text-gray-500 mt-2">Loading history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p>No import history yet. Upload a template to get started!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Date & Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Filename</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Summary</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">By</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <tr
                      onClick={() => setExpandedRowId(expandedRowId === item.id ? null : item.id)}
                      className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                        expandedRowId === item.id ? 'bg-blue-50' : ''
                      } ${index === 0 ? 'bg-green-50/30' : ''}`}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="font-medium">
                              {new Date(item.imported_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(item.imported_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-green-600" />
                          <div>
                            <div className="font-medium text-gray-900 truncate max-w-xs">
                              {item.original_filename}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(item.file_size_kb / 1024).toFixed(2)} MB · {item.file_format}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {item.import_status === 'success' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                            <CheckCircle2 className="w-3 h-3" />
                            Success
                          </span>
                        )}
                        {item.import_status === 'partial' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                            <AlertTriangle className="w-3 h-3" />
                            Partial
                          </span>
                        )}
                        {item.import_status === 'failed' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                            <XCircle className="w-3 h-3" />
                            Failed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex gap-2 flex-wrap">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {item.processes_imported}P
                          </span>
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            {item.process_steps_imported}S
                          </span>
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                            {item.risks_imported}R
                          </span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            {item.controls_imported}C
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-gray-400" />
                          <span className="truncate max-w-xs">{item.user?.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {expandedRowId === item.id ? (
                          <ChevronUp className="w-5 h-5 text-blue-600 mx-auto" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 mx-auto" />
                        )}
                      </td>
                    </tr>
                    {expandedRowId === item.id && (
                      <tr className="bg-blue-50">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="bg-white rounded-lg border border-blue-200 p-4">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <Info className="w-4 h-4 text-blue-600" />
                              Import Details
                            </h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 mb-1">Original Filename:</p>
                                <p className="font-medium text-gray-900">{item.original_filename}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 mb-1">Imported By:</p>
                                <p className="font-medium text-gray-900">{item.user?.name} ({item.user?.email})</p>
                              </div>
                              <div>
                                <p className="text-gray-600 mb-1">File Format:</p>
                                <p className="font-medium text-gray-900">{item.file_format}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 mb-1">Import Duration:</p>
                                <p className="font-medium text-gray-900">{(item.import_duration_ms / 1000).toFixed(2)}s</p>
                              </div>
                            </div>
                            <div className="mt-4 grid grid-cols-4 gap-3">
                              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                <div className="text-2xl font-bold text-blue-700">{item.processes_imported}</div>
                                <div className="text-xs text-blue-600">Processes</div>
                              </div>
                              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                                <div className="text-2xl font-bold text-purple-700">{item.process_steps_imported}</div>
                                <div className="text-xs text-purple-600">Process Steps</div>
                              </div>
                              <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                                <div className="text-2xl font-bold text-orange-700">{item.risks_imported}</div>
                                <div className="text-xs text-orange-600">Risks</div>
                              </div>
                              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                                <div className="text-2xl font-bold text-green-700">{item.controls_imported}</div>
                                <div className="text-xs text-green-600">Controls</div>
                              </div>
                            </div>
                            {item.warnings && item.warnings.length > 0 && (
                              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-yellow-900 mb-2 flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  Warnings:
                                </p>
                                <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                                  {item.warnings.map((warning, idx) => (
                                    <li key={idx}>{warning}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {item.error_message && (
                              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-red-900 mb-2 flex items-center gap-2">
                                  <XCircle className="w-4 h-4" />
                                  Error:
                                </p>
                                <p className="text-sm text-red-800">{item.error_message}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateImportTab;
